import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/deploy/components/ui/Button';
import { Icon } from '@/deploy/components/ui/Icon';
import { Badge } from '@/deploy/components/ui/Badge';
import { Modal } from '@/deploy/components/ui/Modal';
import {
  canRetryRelease,
  containerReleasesService,
  type AcceptedBump,
  type ApplyReleaseInput,
  type ContainerRelease,
  type CreateReleaseInput,
  type ReleaseStatus,
} from '@/deploy/services/container-releases.service';
import { containerFilesService, type FileModificationRemote } from '@/deploy/services/container-files.service';
import type { ArtifactRelease } from '@/deploy/types';
import { formatRelative } from '@/deploy/utils/format';
import { RetryError, RetryNotes, RetryReleaseButton, useRetryRelease } from './RetryRelease';
import styles from './ContainerReleasePanel.module.scss';

const POLL_INTERVAL_ACTIVE = 5_000;

const STATUS_LABEL: Record<ReleaseStatus, string> = {
  'update-pending': 'CI en cours',
  'update-failed': 'CI échouée',
  'review-required': 'Review requise',
  'applying': 'Application…',
  'applied': 'Curation appliquée',
  'pr-merged': 'PR mergée',
  'released': 'Release publiée',
  'cancelled': 'Annulée',
  'failed': 'Échec',
};

const STATUS_TONE: Record<ReleaseStatus, 'info' | 'warning' | 'success' | 'danger' | 'muted'> = {
  'update-pending': 'info',
  'update-failed': 'danger',
  'review-required': 'warning',
  'applying': 'info',
  'applied': 'info',
  'pr-merged': 'info',
  'released': 'success',
  'cancelled': 'muted',
  'failed': 'danger',
};

interface Props {
  projectId: string;
  containerCode: string;
  containerVersion: string | null;
  artifacts: ArtifactRelease[];
  /** Container archivé : historique visible, aucune nouvelle release (le CICD la refuserait). */
  archived?: boolean;
}

export function ContainerReleasePanel({ projectId, containerCode, containerVersion, artifacts, archived = false }: Props) {
  const [active, setActive] = useState<ContainerRelease | null>(null);
  const [history, setHistory] = useState<ContainerRelease[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [pendingMods, setPendingMods] = useState<FileModificationRemote[]>([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [a, h, mods] = await Promise.all([
        containerReleasesService.active(projectId, containerCode),
        containerReleasesService.list(projectId, containerCode, 10),
        containerFilesService.listMods(projectId, containerCode).catch(() => []),
      ]);
      setActive(a);
      setHistory(h);
      setPendingMods(mods);
    } finally {
      setLoading(false);
    }
  }, [projectId, containerCode]);

  useEffect(() => { void reload(); }, [reload]);

  // Relance acceptée : la réponse du POST s'applique tout de suite (la ligne quitte l'échec, la
  // release devient l'active et le polling reprend), puis rechargement sans propager d'erreur
  const onRetried = useCallback(async (updated: ContainerRelease) => {
    setHistory((h) => h.map((r) => (r.id === updated.id ? updated : r)));
    if (!['released', 'cancelled', 'failed', 'update-failed'].includes(updated.status)) setActive(updated);
    await reload().catch(() => { /* best-effort */ });
  }, [reload]);

  // Polling auto si une release est active
  useEffect(() => {
    if (!active || ['released', 'cancelled', 'failed', 'update-failed'].includes(active.status)) return;
    const t = setInterval(() => { void reload(); }, POLL_INTERVAL_ACTIVE);
    return () => clearInterval(t);
  }, [active, reload]);

  // Suggère la version SUIVANTE basée sur la plus haute déjà utilisée (history + containerVersion
  // courant en sync). Évite les collisions quand une release vient d'être publiée mais que la
  // sync auto n'a pas encore rafraîchi containerVersion.
  const suggestedVersion = useMemo(() => {
    const candidates: string[] = [];
    if (containerVersion) candidates.push(containerVersion);
    history.forEach((r) => candidates.push(r.branchName));
    const parsed = candidates
      .map((v) => v.match(/^v?(\d+)\.(\d+)\.(\d+)$/))
      .filter((m): m is RegExpMatchArray => m !== null)
      .map((m) => [Number(m[1]), Number(m[2]), Number(m[3])] as const);
    if (parsed.length === 0) return 'v0.0.1';
    parsed.sort((a, b) => (b[0] - a[0]) || (b[1] - a[1]) || (b[2] - a[2]));
    const [maj, min, patch] = parsed[0];
    return `v${maj}.${min}.${patch + 1}`;
  }, [containerVersion, history]);

  const blockedReason = archived
    ? 'Container archivé : désarchive-le pour lancer une release'
    : active ? `Release ${active.branchName} en cours (status: ${active.status})` : null;

  // Recharge les données fraîches AVANT d'ouvrir la modal (pour avoir les dernières
  // modifs de fichiers + history sans demander un refresh manuel à l'utilisateur).
  const openNewRelease = async () => {
    await reload();
    setShowCreate(true);
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.headerRow}>
        <div>
          <h2 className={styles.title}><Icon name="rocket" size={16} /> Releases</h2>
          <p className={styles.subtitle}>
            {active
              ? <>Release <code>{active.branchName}</code> en cours · {history.length} dans l'historique</>
              : <>Aucune release en cours · {history.length} dans l'historique</>}
          </p>
        </div>
        <Button
          variant="primary"
          leftIcon={<Icon name="plus" size={12} />}
          onClick={() => void openNewRelease()}
          disabled={!!active || archived}
          title={blockedReason ?? 'Créer une nouvelle release'}
        >
          Nouvelle release
        </Button>
      </div>

      {active && (
        <ActiveReleaseBanner
          release={active}
          pendingMods={pendingMods}
          onUpdate={reload}
          projectId={projectId}
          containerCode={containerCode}
        />
      )}

      {history.length > 0 && (
        <section className={styles.history}>
          <h3 className={styles.historyTitle}>Historique des releases</h3>
          <div className={styles.historyList}>
            {history.map((r) => (
              <HistoryRow
                key={r.id}
                release={r}
                projectId={projectId}
                // Relançable seulement si aucune autre release du container ne l'interdit (même règle que le CICD),
                // jamais sur un container archivé (dépôt en lecture seule)
                retryable={!archived && canRetryRelease(r, [active, ...history])}
                onRetried={onRetried}
                onReload={reload}
              />
            ))}
          </div>
        </section>
      )}

      {showCreate && (
        <NewReleaseModal
          projectId={projectId}
          containerCode={containerCode}
          suggestedVersion={suggestedVersion}
          pendingMods={pendingMods}
          artifacts={artifacts}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); void reload(); }}
        />
      )}

      {loading && !active && history.length === 0 && (
        <div className={styles.muted}>Chargement…</div>
      )}
    </div>
  );
}

// ============================================================
// New release modal — auto-curation tout-en-un
// ============================================================
function NewReleaseModal({
  projectId, containerCode, suggestedVersion, pendingMods, artifacts, onClose, onCreated,
}: {
  projectId: string;
  containerCode: string;
  suggestedVersion: string;
  pendingMods: FileModificationRemote[];
  artifacts: ArtifactRelease[];
  onClose: () => void;
  onCreated: () => void;
}) {
  // Bumps détectables localement = artefact dont latestAvailable est > version
  const availableBumps = useMemo(() => {
    return artifacts
      .filter((a) => a.latestAvailable && a.latestAvailable !== a.version)
      .map((a) => ({ name: a.name, repository: a.repository, fromVersion: a.version, toVersion: a.latestAvailable! }));
  }, [artifacts]);

  const [version, setVersion] = useState(suggestedVersion);
  // Map<artifactName, targetVersion> — par défaut, tous les bumps disponibles cochés à leur latestAvailable
  const [acceptedBumps, setAcceptedBumps] = useState<Map<string, string>>(
    () => new Map(availableBumps.map((b) => [b.name, b.toVersion]))
  );
  const [includedPaths, setIncludedPaths] = useState<Set<string>>(
    () => new Set(pendingMods.map((m) => m.path))
  );
  const [commitMessage, setCommitMessage] = useState('');
  const [autoMerge, setAutoMerge] = useState(true);
  const [autoApply, setAutoApply] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleBump = (name: string, latest: string) => setAcceptedBumps((s) => {
    const n = new Map(s);
    if (n.has(name)) n.delete(name); else n.set(name, latest);
    return n;
  });
  const setBumpVersion = (name: string, ver: string) => setAcceptedBumps((s) => {
    const n = new Map(s);
    n.set(name, ver);
    return n;
  });
  const togglePath = (p: string) => setIncludedPaths((s) => {
    const n = new Set(s);
    if (n.has(p)) n.delete(p); else n.add(p);
    return n;
  });

  const valid = /^v\d+\.\d+\.\d+$/.test(version);

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const payload: CreateReleaseInput = {
        newVersion: version,
        includedFileModPaths: Array.from(includedPaths),
        commitMessage: commitMessage.trim() || undefined,
      };
      if (autoApply) {
        const bumps: AcceptedBump[] = Array.from(acceptedBumps.entries()).map(([name, toVersion]) => ({ name, toVersion }));
        payload.autoApply = {
          bumps,
          autoMerge,
          commitMessage: commitMessage.trim() || undefined,
        };
      }
      await containerReleasesService.create(projectId, containerCode, payload);
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur inconnue');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open onClose={onClose}
      title="Nouvelle release"
      subtitle={`Container ${containerCode} — pré-curation + auto-merge si tout est sélectionné`}
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Annuler</Button>
          <Button variant="primary" onClick={submit} disabled={!valid || submitting} loading={submitting}>
            {autoApply
              ? (autoMerge ? 'Lancer la release (auto)' : 'Lancer la release (sans merge)')
              : 'Créer la branche (review manuelle)'}
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.formCols}>
          <label className={styles.field}>
            <span>Numéro de version <em>format vX.Y.Z</em></span>
            <input
              type="text"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="v0.0.139"
            />
          </label>
          <label className={styles.field}>
            <span>Message de commit (optionnel)</span>
            <input
              type="text"
              value={commitMessage}
              onChange={(e) => setCommitMessage(e.target.value)}
              placeholder="feat: nouvelle event de printemps"
            />
          </label>
        </div>

        <div className={styles.field}>
          <span>
            Bumps d'artefacts ({acceptedBumps.size}/{availableBumps.length} sélectionnés)
            <em>versions calculées localement depuis le dernier sync GitHub</em>
          </span>
          {availableBumps.length === 0 ? (
            <div className={styles.modsEmpty}>Tous les artefacts sont déjà à jour.</div>
          ) : (
            <div className={styles.bumpList}>
              {availableBumps.map((b) => {
                const checked = acceptedBumps.has(b.name);
                const target = acceptedBumps.get(b.name) ?? b.toVersion;
                return (
                  <label key={b.name} className={styles.bumpRow}>
                    <input type="checkbox" checked={checked} onChange={() => toggleBump(b.name, b.toVersion)} />
                    <span className={styles.bumpName}>{b.name}</span>
                    <span className={styles.bumpVer}>
                      <code className={styles.dim}>{b.fromVersion}</code>
                      <Icon name="arrow-right" size={11} />
                      <input
                        type="text"
                        value={target}
                        disabled={!checked}
                        onChange={(e) => setBumpVersion(b.name, e.target.value)}
                        className={styles.versionInput}
                      />
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className={styles.field}>
          <span>Modifications de fichiers à inclure ({includedPaths.size}/{pendingMods.length})</span>
          {pendingMods.length === 0 ? (
            <div className={styles.modsEmpty}>Aucune modification en attente sur ce container.</div>
          ) : (
            <div className={styles.modsList}>
              {pendingMods.map((m) => (
                <label key={m.path} className={styles.modRow}>
                  <input
                    type="checkbox"
                    checked={includedPaths.has(m.path)}
                    onChange={() => togglePath(m.path)}
                  />
                  <code className={styles.modPath}>{m.path}</code>
                  <Badge tone={m.type === 'added' ? 'success' : m.type === 'modified' ? 'warning' : 'danger'}>
                    {m.type}
                  </Badge>
                  <span className={styles.modBy}>{m.by}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className={styles.advancedRow}>
          <label className={styles.toggleRow}>
            <input type="checkbox" checked={autoApply} onChange={(e) => setAutoApply(e.target.checked)} />
            <span>
              <strong>Appliquer automatiquement</strong> la curation après update.yml
              <em>(décocher pour review manuelle)</em>
            </span>
          </label>
          {autoApply && (
            <label className={styles.toggleRow}>
              <input type="checkbox" checked={autoMerge} onChange={(e) => setAutoMerge(e.target.checked)} />
              <span>
                <strong>Auto-merger la PR</strong> après application
                <em>(décocher si tu veux d'abord vérifier sur GitHub)</em>
              </span>
            </label>
          )}
        </div>

        <div className={styles.info}>
          <Icon name="rocket" size={12} />
          <span>
            {autoApply
              ? <>Crée la branche <code>{version}</code>, attend <code>update.yml</code>, applique tes choix, {autoMerge ? 'merge la PR' : 'laisse la PR ouverte'}, et déclenche <code>release.yml</code>. Aucune intervention requise.</>
              : <>Crée la branche <code>{version}</code> sur GitHub. Tu pourras curer manuellement via le panneau de review.</>}
          </span>
        </div>
      </div>
    </Modal>
  );
}

// ============================================================
// Active release banner (incl. curation panel)
// ============================================================
function ActiveReleaseBanner({
  release, pendingMods, onUpdate, projectId, containerCode,
}: {
  release: ContainerRelease;
  pendingMods: FileModificationRemote[];
  onUpdate: () => void;
  projectId: string;
  containerCode: string;
}) {
  const [refreshing, setRefreshing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [merging, setMerging] = useState(false);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await containerReleasesService.refresh(projectId, containerCode, release.id);
      onUpdate();
    } finally {
      setRefreshing(false);
    }
  };

  const cancel = async () => {
    if (!confirm(`Annuler la release ${release.branchName} ? La branche distante sera supprimée.`)) return;
    setCancelling(true);
    try {
      await containerReleasesService.cancel(projectId, containerCode, release.id);
      onUpdate();
    } catch (e) {
      alert('Cancel ko: ' + (e instanceof Error ? e.message : 'inconnu'));
    } finally {
      setCancelling(false);
    }
  };

  const merge = async () => {
    if (!confirm(`Merger la PR #${release.pullRequestNumber} ? La branche sera fusionnée dans main et release.yml va builder l'image Docker.`)) return;
    setMerging(true);
    try {
      await containerReleasesService.merge(projectId, containerCode, release.id);
      // Petit délai pour laisser le poller détecter le merge
      setTimeout(onUpdate, 1500);
    } catch (e) {
      alert('Merge ko: ' + (e instanceof Error ? e.message : 'inconnu'));
    } finally {
      setMerging(false);
    }
  };

  const canMerge = release.status === 'applied' && release.pullRequestNumber != null;
  const isFinal = ['released', 'cancelled', 'failed'].includes(release.status);

  return (
    <div className={[styles.banner, styles[`banner_${release.status}`]].join(' ')}>
      <header className={styles.bannerHeader}>
        <div className={styles.bannerTitle}>
          <Icon name="rocket" size={16} />
          <span>Release {release.branchName}</span>
          <Badge tone={STATUS_TONE[release.status]}>{STATUS_LABEL[release.status]}</Badge>
          <RetryNotes release={release} />
        </div>
        <div className={styles.bannerActions}>
          {canMerge && (
            <Button variant="primary" size="sm" leftIcon={<Icon name="check" size={11} />}
              onClick={merge} loading={merging}>
              Merger la PR #{release.pullRequestNumber}
            </Button>
          )}
          <Button variant="ghost" size="sm" leftIcon={<Icon name="refresh" size={11} />}
            onClick={refresh} loading={refreshing}>Refresh</Button>
          {release.pullRequestUrl && (
            <a href={release.pullRequestUrl} target="_blank" rel="noreferrer">
              <Button variant="ghost" size="sm" leftIcon={<Icon name="external" size={11} />}>
                PR #{release.pullRequestNumber}
              </Button>
            </a>
          )}
          {!isFinal && (
            <Button variant="ghost" size="sm" leftIcon={<Icon name="x" size={11} />}
              onClick={cancel} loading={cancelling}>Annuler</Button>
          )}
        </div>
      </header>

      {release.status === 'applied' && (
        <div className={styles.info}>
          <Icon name="check" size={12} />
          <span>
            Curation appliquée et pushée sur GitHub. <strong>Étape suivante :</strong> merger la PR pour
            déclencher <code>release.yml</code> (tag + image Docker).
          </span>
        </div>
      )}

      <Timeline release={release} />

      {release.mergedWithoutCuration && (
        <div className={styles.warning}>
          <Icon name="warn" size={14} />
          <span>
            ⚠ Cette release a été mergée sur GitHub <strong>sans passer par la curation du panel</strong>.
            Toutes les bumps proposées par <code>update.yml</code> ont donc été acceptées telles quelles.
          </span>
        </div>
      )}

      {release.status === 'review-required' && (
        <CurationPanel release={release} pendingMods={pendingMods} onApplied={onUpdate}
          projectId={projectId} containerCode={containerCode} />
      )}

      {release.status === 'released' && release.dockerImage && (
        <div className={styles.releasedBox}>
          <Icon name="check" size={14} />
          <code>{release.dockerImage}</code>
          {release.githubReleaseUrl && (
            <a href={release.githubReleaseUrl} target="_blank" rel="noreferrer">
              GitHub Release ↗
            </a>
          )}
        </div>
      )}

      {(release.status === 'update-failed' || release.status === 'failed') && release.errors && release.errors.length > 0 && (
        <div className={styles.errorsBox}>
          <strong>Erreurs :</strong>
          {release.errors.map((e, i) => (
            <div key={i} className={styles.errorLine}>
              <code>{e.phase}</code> · {e.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Curation panel (Phase 3)
// ============================================================
function CurationPanel({
  release, pendingMods, onApplied, projectId, containerCode,
}: {
  release: ContainerRelease;
  pendingMods: FileModificationRemote[];
  onApplied: () => void;
  projectId: string;
  containerCode: string;
}) {
  const proposed = release.proposedBumps ?? [];
  const skipped = release.skippedBumps ?? [];
  const snapshots = release.fileModSnapshots ?? [];

  const [acceptedNames, setAcceptedNames] = useState<Set<string>>(
    () => new Set(proposed.map((p) => p.name))
  );
  const [manualBumps, setManualBumps] = useState<Map<string, string>>(new Map());
  const [includedPaths, setIncludedPaths] = useState<Set<string>>(
    () => new Set(snapshots.map((s) => s.path))
  );
  const [commitMessage, setCommitMessage] = useState('');
  const [autoMerge, setAutoMerge] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshedMods = useMemo(() => {
    const known = new Set(snapshots.map((s) => s.path));
    return pendingMods.filter((m) => !known.has(m.path));
  }, [pendingMods, snapshots]);

  const apply = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const input: ApplyReleaseInput = {
        acceptedBumpNames: Array.from(acceptedNames),
        manualBumps: Array.from(manualBumps.entries()).map(([name, toVersion]) => ({ name, toVersion })),
        appliedFileModPaths: Array.from(includedPaths),
        commitMessage: commitMessage.trim() || undefined,
        autoMerge,
      };
      await containerReleasesService.apply(projectId, containerCode, release.id, input);
      onApplied();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur inconnue');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleAccepted = (name: string) =>
    setAcceptedNames((s) => { const n = new Set(s); if (n.has(name)) n.delete(name); else n.add(name); return n; });

  const toggleIncluded = (p: string) =>
    setIncludedPaths((s) => { const n = new Set(s); if (n.has(p)) n.delete(p); else n.add(p); return n; });

  const toggleManual = (s: { name: string; latestAvailable: string }) =>
    setManualBumps((cur) => {
      const n = new Map(cur);
      if (n.has(s.name)) n.delete(s.name);
      else n.set(s.name, s.latestAvailable);
      return n;
    });

  return (
    <div className={styles.curation}>
      {error && <div className={styles.error}>{error}</div>}

      <section>
        <h4>
          Bumps proposés par <code>update.yml</code>
          <Badge tone="info">{acceptedNames.size}/{proposed.length} acceptés</Badge>
        </h4>
        {proposed.length === 0 && (
          <div className={styles.muted}>Aucun bump proposé — tous les artefacts sont déjà à jour.</div>
        )}
        <div className={styles.bumpList}>
          {proposed.map((b) => (
            <label key={b.name} className={styles.bumpRow}>
              <input type="checkbox" checked={acceptedNames.has(b.name)} onChange={() => toggleAccepted(b.name)} />
              <span className={styles.bumpName}>{b.name}</span>
              <span className={styles.bumpVer}>
                <code className={styles.dim}>{b.fromVersion ?? '?'}</code>
                <Icon name="arrow-right" size={11} />
                <code>{b.toVersion}</code>
              </span>
            </label>
          ))}
        </div>
      </section>

      {skipped.length > 0 && (
        <section>
          <h4>
            Bumps disponibles mais ignorés par <code>update.yml</code>
            <Badge tone="muted">{skipped.length}</Badge>
            <span className={styles.dim}>(probablement <code>checkUpdate: false</code>)</span>
          </h4>
          <div className={styles.bumpList}>
            {skipped.map((s) => (
              <label key={s.name} className={styles.bumpRow}>
                <input type="checkbox"
                  checked={manualBumps.has(s.name)}
                  onChange={() => toggleManual(s)} />
                <span className={styles.bumpName}>{s.name}</span>
                <span className={styles.bumpVer}>
                  <code className={styles.dim}>{s.currentVersion}</code>
                  <Icon name="arrow-right" size={11} />
                  <code>{s.latestAvailable}</code>
                </span>
              </label>
            ))}
          </div>
        </section>
      )}

      <section>
        <h4>
          Modifications de fichiers à inclure
          <Badge tone="info">{includedPaths.size}/{snapshots.length} sélectionnées</Badge>
        </h4>
        {snapshots.length === 0 && (
          <div className={styles.muted}>Aucune modification snapshotée pour cette release.</div>
        )}
        <div className={styles.modsList}>
          {snapshots.map((s) => (
            <label key={s.path} className={styles.modRow}>
              <input type="checkbox" checked={includedPaths.has(s.path)} onChange={() => toggleIncluded(s.path)} />
              <code className={styles.modPath}>{s.path}</code>
              <Badge tone={s.type === 'added' ? 'success' : s.type === 'modified' ? 'warning' : 'danger'}>
                {s.type}
              </Badge>
              <span className={styles.modBy}>{s.by}</span>
            </label>
          ))}
        </div>
        {refreshedMods.length > 0 && (
          <div className={styles.warning}>
            <Icon name="warn" size={12} />
            <span>
              {refreshedMods.length} modif{refreshedMods.length > 1 ? 's' : ''} apparue{refreshedMods.length > 1 ? 's' : ''} après
              le snapshot — non incluse{refreshedMods.length > 1 ? 's' : ''}. Crée une nouvelle release pour les inclure.
            </span>
          </div>
        )}
      </section>

      <label className={styles.field}>
        <span>Message de commit pour la curation (optionnel)</span>
        <input
          type="text"
          value={commitMessage}
          onChange={(e) => setCommitMessage(e.target.value)}
          placeholder="panel: garder PalaMod 0.0.50, ajouter event spring"
        />
      </label>

      <label className={styles.autoMergeRow}>
        <input
          type="checkbox"
          checked={autoMerge}
          onChange={(e) => setAutoMerge(e.target.checked)}
        />
        <span>
          <strong>Merger automatiquement la PR</strong> après application
          <em>(décocher si tu veux d'abord inspecter sur GitHub)</em>
        </span>
      </label>

      <div className={styles.curationActions}>
        <Button variant="primary" onClick={apply} disabled={submitting} loading={submitting}>
          {autoMerge ? 'Appliquer & merger' : 'Appliquer (sans merger)'}
        </Button>
      </div>
    </div>
  );
}

// ============================================================
// Timeline & history row
// ============================================================
const STEPS: { key: string; label: string }[] = [
  { key: 'pushed', label: 'Branche poussée' },
  { key: 'updated', label: 'CI update.yml' },
  { key: 'reviewed', label: 'Curation appliquée' },
  { key: 'merged', label: 'PR mergée' },
  { key: 'released', label: 'Image Docker' },
];

function Timeline({ release }: { release: ContainerRelease }) {
  const reachedIdx = (() => {
    if (release.status === 'released') return 5;
    if (release.status === 'pr-merged') return 4;
    if (release.status === 'applied') return 3;
    if (release.status === 'review-required') return 2;
    if (release.status === 'update-pending') return 1;
    if (release.status === 'cancelled' || release.status === 'failed' || release.status === 'update-failed') {
      // figé là où il était
      return release.appliedAt ? 3 : release.proposedAt ? 2 : 1;
    }
    return 1;
  })();
  return (
    <ol className={styles.timeline}>
      {STEPS.map((s, i) => {
        const reached = i < reachedIdx;
        const current = i === reachedIdx - 1;
        return (
          <li key={s.key} className={[
            styles.tlStep,
            reached && styles.tlReached,
            current && styles.tlCurrent,
          ].filter(Boolean).join(' ')}>
            <span className={styles.tlDot} />
            <span className={styles.tlLabel}>{s.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

function HistoryRow({ release, projectId, retryable, onRetried, onReload }: {
  release: ContainerRelease;
  projectId: string;
  retryable: boolean;
  onRetried: (updated: ContainerRelease) => Promise<void>;
  onReload: () => Promise<void>;
}) {
  // Refus 409 : la vue n'était plus à jour, on recharge le panneau
  const retry = useRetryRelease(projectId, release, retryable, onRetried, onReload);
  const failed = release.status === 'update-failed' || release.status === 'failed';
  const lastError = failed && release.errors && release.errors.length > 0
    ? release.errors[release.errors.length - 1]
    : null;
  return (
    <article className={styles.historyRow}>
      <div className={styles.historyTop}>
        <div className={styles.historyMain}>
          <code className={styles.historyVersion}>{release.branchName}</code>
          <Badge tone={STATUS_TONE[release.status]}>{STATUS_LABEL[release.status]}</Badge>
          {release.mergedWithoutCuration && <Badge tone="warning">bypass</Badge>}
          <RetryNotes release={release} />
        </div>
        {retryable && <RetryReleaseButton state={retry} />}
      </div>
      {lastError && (
        <div className={styles.historyError}>
          <code>{lastError.phase}</code> · {lastError.message}
        </div>
      )}
      {retryable && <RetryError state={retry} />}
      <div className={styles.historyMeta}>
        <span>par {release.initiatedByName}</span>
        <span>·</span>
        <span>{formatRelative(release.initiatedAt)}</span>
        {release.pullRequestUrl && (
          <>
            <span>·</span>
            <a href={release.pullRequestUrl} target="_blank" rel="noreferrer">PR #{release.pullRequestNumber}</a>
          </>
        )}
        {release.githubReleaseUrl && (
          <>
            <span>·</span>
            <a href={release.githubReleaseUrl} target="_blank" rel="noreferrer">GH Release</a>
          </>
        )}
      </div>
    </article>
  );
}
